import { useState } from 'react'
import { Box, Button, HStack, Stack, Text, Textarea, VStack } from '@chakra-ui/react'
import ReactMarkdown from 'react-markdown'
import { FiTrash2 } from 'react-icons/fi'
import { useAuth } from '../../hooks/useAuth'
import { useAddLegislatorNote, useDeleteLegislatorNote, useLegislatorNotes } from '../../hooks/useLobbyDay'
import { proseContentCss } from '../../theme/proseStyles'
import type { LegislatorNoteDto } from '../../types/lobbyDay'

interface LegislatorNotesPanelProps {
  legislatorId: string
  eventId?: string
}

function formatNoteDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

/** Reads the server's error message off an axios error, falling back to a generic one. */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response
    if (typeof response?.data?.message === 'string' && response.data.message) {
      return response.data.message
    }
  }
  return fallback
}

/**
 * Reusable running-note panel for a single legislator (L13). Task 5 wires this onto two
 * surfaces (the legislator detail drawer/page and the reconciliation grid). Not routed itself.
 */
export function LegislatorNotesPanel({ legislatorId, eventId }: LegislatorNotesPanelProps) {
  const { user } = useAuth()
  const { data: notes, isLoading } = useLegislatorNotes(legislatorId)
  const addNote = useAddLegislatorNote(legislatorId)
  const deleteNote = useDeleteLegislatorNote(legislatorId)

  const [body, setBody] = useState('')
  // Shared between add and delete -- both surface server errors the same inline-text way,
  // matching the house pattern (no toast system here).
  const [noteError, setNoteError] = useState<string | null>(null)

  const sortedNotes: LegislatorNoteDto[] = [...(notes ?? [])].sort(
    (a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0),
  )

  const isBusy = addNote.isPending || deleteNote.isPending

  const handleAdd = () => {
    const trimmed = body.trim()
    if (!trimmed) return
    setNoteError(null)
    addNote.mutate(
      { body: trimmed, lobbyDayEventId: eventId ?? null },
      {
        onSuccess: () => setBody(''),
        onError: (err) => setNoteError(extractErrorMessage(err, 'Failed to add note. Please try again.')),
      },
    )
  }

  const handleDelete = (noteId: string) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return
    setNoteError(null)
    deleteNote.mutate(noteId, {
      onError: (err) => setNoteError(extractErrorMessage(err, 'Failed to delete note. Please try again.')),
    })
  }

  return (
    <Stack gap={4}>
      <VStack align="stretch" gap={3}>
        {isLoading && <Text color="ink.2">Loading notes…</Text>}
        {!isLoading && sortedNotes.length === 0 && (
          <Text color="ink.2">No notes yet.</Text>
        )}
        {sortedNotes.map((note) => (
          <Box
            key={note.id}
            data-testid={`legislator-note-${note.id}`}
            borderWidth="1px"
            borderColor="border"
            borderRadius="md"
            p={3}
          >
            <HStack justify="space-between" align="start" gap={2}>
              <Text fontSize="sm" color="ink.2">
                {note.authorName} · {formatNoteDate(note.createdAt)}
                {note.eventName ? ` · (${note.eventName})` : ''}
              </Text>
              {note.createdByUserId === user?.id && (
                <Button
                  type="button"
                  size="sm"
                  minH="44px"
                  minW="44px"
                  px={2}
                  variant="ghost"
                  colorPalette="red"
                  aria-label="Delete note"
                  disabled={isBusy}
                  onClick={() => handleDelete(note.id)}
                >
                  <FiTrash2 />
                </Button>
              )}
            </HStack>
            <Box mt={1} color="ink" css={proseContentCss}>
              <ReactMarkdown>{note.body}</ReactMarkdown>
            </Box>
          </Box>
        ))}
      </VStack>

      <Stack gap={2}>
        <Textarea
          placeholder="Add a note…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          borderColor="border"
          color="ink"
          disabled={isBusy}
        />
        {!!noteError && <Text color="danger.fg">{noteError}</Text>}
        <Button
          type="button"
          alignSelf="flex-start"
          minH="44px"
          colorPalette="brand"
          disabled={isBusy || !body.trim()}
          loading={addNote.isPending}
          onClick={handleAdd}
        >
          Add note
        </Button>
      </Stack>
    </Stack>
  )
}
